import { useState } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Box, Heading, HStack, Text } from "@chakra-ui/react";
import { PageCard } from "components/page-card";
import { Table, type TableColumn } from "components/table/table";
import { useFormatMoneyCentsConverter } from "hooks/use-format-money";
import type {
  FinancialOverviewNode,
  FinancialsOverviewData,
} from "modules/landlord-portal/models/financials";

type FundRow = {
  id: string;
  label: string;
  value: string | number;
  // Indents the row as a child of an expandable parent row.
  isSubItem?: boolean;
  // Marks the row as a parent that can be expanded to reveal its sub-rows (shows a chevron).
  isExpandable?: boolean;
  // Current open/closed state of an expandable row (controls the chevron direction).
  isExpanded?: boolean;
  // Toggles this expandable row open/closed when its label is clicked.
  onToggle?: () => void;
};

function ExpandableRow({ row }: { row: FundRow }) {
  return (
    <HStack
      as={"button"}
      type={"button"}
      onClick={row.onToggle}
      gap={1}
      w={"full"}
    >
      {row.isExpanded ? (
        <ChevronDownIcon boxSize={5} />
      ) : (
        <ChevronRightIcon boxSize={5} />
      )}
      <Text>{row.label}</Text>
    </HStack>
  );
}

function headerColumn(header: string): TableColumn<FundRow>[] {
  return [
    {
      id: "label",
      label: (
        <Heading as={"h3"} fontSize={"lg"}>
          {header}
        </Heading>
      ),
      Cell: ({ row }) =>
        row.isExpandable ? (
          <ExpandableRow row={row} />
        ) : (
          // this is to align for non-expandable rows that has
          // expandable rows
          <HStack gap={1} pl={row.isSubItem ? 6 : 0}>
            <Box boxSize={5} />
            <Text>{row.label}</Text>
          </HStack>
        ),
    },
    {
      id: "value",
      align: "right",
      // Mute sub-item values so they read as part of the parent's total.
      Cell: ({ row, value }) => (
        <Text
          color={row.isSubItem ? "gray.600" : undefined}
          fontWeight={!row.isSubItem ? 600 : undefined}
        >
          {value}
        </Text>
      ),
    },
  ];
}

function FinancialSection({
  header,
  nodes,
}: {
  header: string;
  nodes?: FinancialOverviewNode[];
}) {
  const formatMoney = useFormatMoneyCentsConverter();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const rows: FundRow[] = [];
  nodes?.forEach((node) => {
    const hasChildren = !!node.children?.length;
    const isExpanded = expanded[node.label] ?? false;

    // display all the field label and its value (parent)
    rows.push({
      id: node.label,
      label: node.label,
      value: formatMoney(node.value),
      isExpandable: hasChildren,
      isExpanded: hasChildren ? isExpanded : undefined,
      onToggle: hasChildren
        ? () =>
            setExpanded((prev) => ({
              ...prev,
              [node.label]: !prev[node.label],
            }))
        : undefined,
    });

    // if the caret-down was clicked and expanded display the children items
    if (hasChildren && isExpanded) {
      node.children?.forEach((child) => {
        rows.push({
          id: `${node.label}-${child.label}`,
          label: child.label,
          value: formatMoney(child.value),
          isSubItem: true,
        });
      });
    }
  });

  return <Table columns={headerColumn(header)} rows={rows} />;
}

export function FinancialOverview({ data }: { data?: FinancialsOverviewData }) {
  const overview = data?.financial_entity_overview;
  return (
    <PageCard heading={"Financial Overview"}>
      <FinancialSection
        header={"Balanced and reserved funds"}
        nodes={overview?.balance_and_reserved_funds}
      />
      <FinancialSection header={"Payables"} nodes={overview?.payables} />
      <FinancialSection header={"Receivables"} nodes={overview?.receivables} />
    </PageCard>
  );
}
